import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TableBody,
  Typography,
  useTheme,
  Box,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Block as BlockIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../components/@extended/CustomTable';
import { Columns2 } from './SponsorAdminColumns';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useApplyDashboardPreferencesMutation, useGetDashboardConfigMasterQuery } from '../../store/slices/apiSlice';
import CreateAdmin from '../CreateSponsor/CreateAdmin';
import CustomCard from '../../components/@extended/CustomCard';
import CustomButton from '../../components/@extended/CustomButton';

const SponsorAdmin = ({ open, data, isLoading, type = 'sponsor' }) => {
  const theme = useTheme();
  const columns = Columns2();
  const { sponsorId } = useParams();

  // State for managing menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const openMenu = Boolean(anchorEl);

  // State for dashboard config dialog
  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
  const [selectedWidgets, setSelectedWidgets] = useState([]);

  const { data: widgets = [], isLoading: widgetsLoading } = useGetDashboardConfigMasterQuery({});
  const [applyDashboardPreferences, { isLoading: saveLoading }] = useApplyDashboardPreferencesMutation();

  const [error, setError] = useState(null);

  // Save user preferences
  const saveUserPreferences = async () => {
    setError(null);
    try {
      const payload = {
        userId: selectedUser?.user?.id || selectedUser?.id,
        preferenceIds: selectedWidgets,
      };

      const response = await applyDashboardPreferences(payload);
      if (!response.data) throw new Error('Failed to save preferences');

      handleDashboardDialogClose();
      console.log('Preferences saved successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleReportingDashboard = () => {
    setAnchorEl(null);
    const preferenceIds = selectedUser?.user?.dashboardPreferences?.map((pref) => String(pref?.preferenceId)) || [];
    setSelectedWidgets(preferenceIds);
    setDashboardDialogOpen(true);
  };

  const handleDashboardDialogClose = () => {
    setDashboardDialogOpen(false);
    setSelectedUser(null);
    setSelectedWidgets([]);
    setError('');
  };

  const handleWidgetToggle = (widgetId) => {
    const widgetIdStr = String(widgetId);

    setSelectedWidgets((prevSelected) => {
      const currentSelected = [...prevSelected];

      const existingIndex = currentSelected.indexOf(widgetIdStr);

      if (existingIndex !== -1) {
        currentSelected.splice(existingIndex, 1);
      } else {
        currentSelected.push(widgetIdStr);
      }

      return currentSelected;
    });
  };

  if (isLoading) {
    return <></>;
  }

  // Enhanced columns with Actions column
  const enhancedColumns = [
    ...columns,
    {
      id: 'actions',
      label: 'Actions',
      align: 'center',
      style: { minWidth: 100 },
    },
  ];

  return (
    <Box>
      <CustomCard
        title={
          <Typography variant="h6" fontWeight={600} color={theme.palette.grey[800]}>
            {type === 'fi_admin' ? 'Finance Admins' : 'Sponsor Admins'}
          </Typography>
        }
        action={
          <Box sx={{ display: 'flex', columnGap: 2, alignItems: 'center' }}>
            <CreateAdmin open={open} sponsorId={sponsorId} type={type} />
          </Box>
        }
      >
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="sticky table">
            <CustomTableHead>
              <TableRow>
                {enhancedColumns.map((column) => (
                  <CustomTableHeadCell key={column.id} align={column.align} sx={{ ...column.style }}>
                    {column.label}
                  </CustomTableHeadCell>
                ))}
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {data && data.length > 0 ? (
                data.map((row) => (
                  <CustomTableRow key={row?.id} hover role="checkbox" tabIndex={-1}>
                    <TableCell>
                      {row?.user?.firstName} {row?.user?.lastName}
                    </TableCell>
                    <TableCell>{row?.user?.email}</TableCell>
                    <TableCell>{row?.user?.contactNumber}</TableCell>
                    <TableCell>{moment(row?.createdAt).format('DD/MM/YYYY')}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        aria-label="more"
                        id={`menu-button-${row?.id}`}
                        aria-controls={openMenu && selectedUser?.id === row?.id ? 'user-menu' : undefined}
                        aria-expanded={openMenu && selectedUser?.id === row?.id ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={(event) => handleMenuClick(event, row)}
                        sx={{
                          color: theme.palette.grey[600],
                          '&:hover': {
                            backgroundColor: theme.palette.grey[100],
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </CustomTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={enhancedColumns.length}>
                    <Typography variant="subtitle1" color="initial">
                      No Admins Found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Floating Action Menu */}
        <Menu
          id="user-menu"
          MenuListProps={{
            'aria-labelledby': `menu-button-${selectedUser?.id}`,
          }}
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          sx={{
            '& .MuiPaper-root': {
              minWidth: 200,
              boxShadow: theme.shadows[8],
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {/* <MenuItem onClick={handleViewProfile}>
            <ListItemIcon>
              <ViewIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>View Profile</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleEditUser}>
            <ListItemIcon>
              <EditIcon fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>Edit User</ListItemText>
          </MenuItem>

          <Divider /> */}

          <MenuItem onClick={handleReportingDashboard}>
            <ListItemIcon>
              <DashboardIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Reporting Dashboard</ListItemText>
          </MenuItem>

          {/* <MenuItem onClick={handlePermissions}>
            <ListItemIcon>
              <SecurityIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Manage Permissions</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleUserSettings}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>User Settings</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem onClick={handleBlockUser}>
            <ListItemIcon>
              <BlockIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Block/Unblock User</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={handleDeleteUser}
            sx={{
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.light + '20',
              },
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete User</ListItemText>
          </MenuItem> */}
        </Menu>

        {/* Dashboard Configuration Dialog */}
        <Dialog
          open={dashboardDialogOpen}
          onClose={handleDashboardDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: theme.shadows[10],
            },
          }}
        >
          <DialogTitle>
            <Typography variant="h5" fontWeight={600} color={theme.palette.text.primary}>
              Configure Reporting Dashboard
            </Typography>
            <Typography variant="body2" color={theme.palette.text.secondary} sx={{ mt: 0.5 }}>
              Select widgets to display on the dashboard for{' '}
              <strong>
                {selectedUser?.user?.firstName} {selectedUser?.user?.lastName}
              </strong>
            </Typography>
          </DialogTitle>

          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {widgetsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading widgets...
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom color={theme.palette.text.primary}>
                  Available Widgets
                </Typography>
                <FormGroup>
                  <Grid container spacing={2}>
                    {widgets.map((widget) => (
                      <Grid item xs={12} sm={6} key={widget.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedWidgets.includes(String(widget.id))}
                              onChange={() => handleWidgetToggle(widget.id)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" fontWeight={500}>
                                {widget.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                color={theme.palette.text.secondary}
                                sx={{ display: 'block' }}
                              >
                                Type: {widget.type}
                              </Typography>
                            </Box>
                          }
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            p: 1,
                            m: 0,
                            width: '100%',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>

                {selectedWidgets.length > 0 && (
                  <Box mt={3}>
                    <Typography variant="body2" color={theme.palette.primary.main}>
                      {selectedWidgets.length} widget(s) selected
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <CustomButton onClick={handleDashboardDialogClose} variant="outlined" disabled={saveLoading}>
              Cancel
            </CustomButton>
            <CustomButton
              onClick={saveUserPreferences}
              variant="contained"
              disabled={saveLoading || widgetsLoading}
              startIcon={saveLoading && <CircularProgress size={16} />}
            >
              {saveLoading ? 'Saving...' : 'Save Configuration'}
            </CustomButton>
          </DialogActions>
        </Dialog>
      </CustomCard>
    </Box>
  );
};

export default SponsorAdmin;
